import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { T } from '@design/index';
import { teleApi, teleSocket, type TeleConsultation } from './teleApi';
import { useTeleCall, type CallState } from './useTeleCall';

/**
 * The patient's teleconsultation on the web — the SAME session the doctor
 * app and the doctor web see. Consent → waiting room → the doctor starts →
 * the call → the end; every step follows the server, and the call reads
 * "connected" only when the video SDK says it joined.
 *
 * Route: /patient/telemedicine/consult/:appointmentId
 */
export default function TeleConsultPage() {
  const { appointmentId = '' } = useParams();
  const navigate = useNavigate();
  const [consult, setConsult] = useState<TeleConsultation | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [doctorHere, setDoctorHere] = useState(false);
  const [socketUp, setSocketUp] = useState(true);

  const id = consult?.id ?? null;
  const live = consult?.status === 'IN_PROGRESS';
  const fetchToken = useCallback(() => teleApi.token(id!), [id]);
  const call = useTeleCall(live && id ? fetchToken : null);
  const callRef = useRef(call);
  callRef.current = call;

  // Open once: the consultation for this appointment (not changing its state).
  useEffect(() => {
    let alive = true;
    teleApi.open(appointmentId).then((c) => alive && setConsult(c), (e) =>
      alive && setErr((e as { message?: string })?.message ?? 'This consultation could not be opened.'));
    return () => { alive = false; };
  }, [appointmentId]);

  // The room: status, the doctor's presence, reconnects. One socket per
  // screen, closed with it — never a listener left for the next consultation.
  useEffect(() => {
    if (!id) return;
    const s = teleSocket();
    if (!s) return;
    const join = () => s.emit('telemedicine:join', id);
    s.on('connect', () => {
      setSocketUp(true);
      join();
      teleApi.get(id).then(setConsult, () => {});
    });
    s.on('disconnect', () => setSocketUp(false));
    s.on('telemedicine:status', (e: { sessionId: string; status: string; session: TeleConsultation }) => {
      if (e.sessionId !== id) return;
      setConsult(e.session);
      if (['ENDED', 'COMPLETED', 'CANCELLED'].includes(e.status)) void callRef.current.leave();
    });
    s.on('telemedicine:participant-joined', (e: { sessionId: string; role: string }) => {
      if (e.sessionId === id && e.role === 'doctor') setDoctorHere(true);
    });
    s.on('telemedicine:participant-left', (e: { sessionId: string; role: string }) => {
      if (e.sessionId === id && e.role === 'doctor') setDoctorHere(false);
    });
    return () => {
      s.emit('telemedicine:leave', id);
      s.removeAllListeners();
      s.disconnect();
    };
  }, [id]);

  // The doctor started → join the call automatically.
  useEffect(() => {
    if (live && call.state === 'IDLE') void call.join();
  }, [live, call]);

  const enter = async () => {
    if (!consult || busy) return;
    setBusy(true);
    setErr(null);
    try {
      setConsult(await teleApi.wait(consult.id));
    } catch (e) {
      setErr((e as { message?: string })?.message ?? 'Could not enter the waiting room.');
    } finally { setBusy(false); }
  };

  const leave = async () => {
    await call.leave();
    if (consult && !['ENDED', 'COMPLETED', 'CANCELLED'].includes(consult.status)) {
      try { await teleApi.leave(consult.id); } catch { /* leaving is best-effort */ }
    }
    navigate('/patient/care-services/telemedicine');
  };

  const openChat = async () => {
    if (!consult) return;
    try {
      const { conversationId } = await teleApi.chat(consult.id);
      navigate(`/patient/my-chat?c=${encodeURIComponent(conversationId)}`);
    } catch (e) {
      setErr((e as { message?: string })?.message ?? 'The chat could not be opened.');
    }
  };

  const status = consult?.status;
  const callText: Record<CallState, string> = {
    IDLE: 'Starting…',
    REQUESTING_TOKEN: 'Getting ready…',
    CONNECTING: 'Connecting to the doctor…',
    CONNECTED: call.remotePresent ? 'Doctor connected' : 'Connected · waiting for the doctor…',
    RECONNECTING: 'Connection lost · reconnecting…',
    DISCONNECTED: 'Disconnected',
    ENDED: 'You left the call',
    FAILED: call.error ?? 'The call could not connect',
  };
  const headline =
    !consult ? (err ? 'Could not open this consultation' : 'Opening…')
      : status === 'SCHEDULED' ? 'Enter the waiting room when you are ready'
        : status === 'WAITING' ? (doctorHere ? 'The doctor is here and will start shortly' : 'You are in the waiting room')
          : status === 'IN_PROGRESS' ? callText[call.state]
            : status === 'CANCELLED' ? 'This consultation was cancelled'
              : 'This consultation has ended';
  const inCall = call.state === 'CONNECTED' || call.state === 'RECONNECTING';
  const btn = (bg: string) => ({ padding: '10px 16px', borderRadius: 10, border: 'none', background: bg, color: '#fff', fontFamily: T.font, fontSize: 13, fontWeight: 700, cursor: 'pointer' });
  const round = (on: boolean) => ({ width: 48, height: 48, borderRadius: 24, border: 'none', background: on ? 'rgba(255,255,255,0.22)' : '#DC2626', color: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 700 });

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: 16, fontFamily: T.font, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontSize: 18, fontWeight: 800, color: T.text }}>Video consultation{consult?.doctorName ? ` · ${consult.doctorName}` : ''}</div>
      {!socketUp && consult && <div style={{ fontSize: 12, color: '#B45309' }}>Reconnecting to live updates…</div>}
      <div style={{ padding: 14, borderRadius: 12, background: '#0F766E14', border: '1px solid #0F766E40', fontSize: 14, fontWeight: 700, color: T.text }}>
        {headline}
        {consult?.durationSeconds != null && <span style={{ fontWeight: 500, color: T.textSec }}> · {Math.round(consult.durationSeconds / 60)} min</span>}
      </div>
      {err && <div style={{ padding: 10, borderRadius: 10, background: '#DC262610', color: '#B91C1C', fontSize: 12.5 }}>{err}</div>}

      {status === 'SCHEDULED' && (
        <>
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: T.text, lineHeight: 1.5 }}>
            <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} style={{ marginTop: 3 }} />
            I consent to this teleconsultation. I understand it is not suitable for emergencies, that the doctor may advise an in-person visit, and that this consent is recorded.
          </label>
          <button disabled={!ack || busy} onClick={() => void enter()} style={{ ...btn('#0F766E'), opacity: !ack || busy ? 0.5 : 1 }}>Enter waiting room</button>
        </>
      )}

      {live && (
        <div style={{ position: 'relative', background: '#0F172A', borderRadius: 16, overflow: 'hidden', height: 460 }}>
          {/* Always mounted, so the doctor's video plays the moment it arrives. */}
          <div ref={call.remoteRef} style={{ position: 'absolute', inset: 0, visibility: call.remoteVideo ? 'visible' : 'hidden' }} />
          {!call.remoteVideo && <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#fff', fontSize: 14 }}>{callText[call.state]}</div>}
          <div style={{ position: 'absolute', right: 12, top: 12, width: 150, height: 110, borderRadius: 10, overflow: 'hidden', border: '2px solid #fff', background: '#1E293B' }}>
            <div ref={call.localRef} style={{ width: '100%', height: '100%', visibility: inCall && call.camOn ? 'visible' : 'hidden' }} />
          </div>
          {inCall && (
            <div style={{ position: 'absolute', bottom: 14, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 10 }}>
              <button onClick={() => void call.toggleMic()} style={round(call.micOn)}>{call.micOn ? 'Mute' : 'Unmute'}</button>
              <button onClick={() => void call.toggleCam()} style={round(call.camOn)}>{call.camOn ? 'Cam off' : 'Cam on'}</button>
              {call.canSwitchCamera && <button onClick={() => void call.switchCamera()} style={round(true)}>Flip</button>}
              {call.canChooseSpeaker && <button onClick={() => void call.nextSpeaker()} style={round(true)}>Output</button>}
            </div>
          )}
          {(call.state === 'FAILED' || call.state === 'DISCONNECTED') && (
            <button onClick={() => void call.join()} style={{ ...btn('#0F766E'), position: 'absolute', left: 14, bottom: 14 }}>Rejoin</button>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {consult && status !== 'ENDED' && status !== 'COMPLETED' && status !== 'CANCELLED' && (
          <button onClick={() => void openChat()} style={btn(T.navy)}>Message the doctor</button>
        )}
        <button onClick={() => void leave()} style={btn(status === 'WAITING' || live ? '#DC2626' : '#64748B')}>
          {status === 'WAITING' || live ? 'Leave' : 'Back'}
        </button>
      </div>
    </div>
  );
}
